import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search, Check, CircleMinus, CircleCheck, CalendarClock, Edit2, Lock } from 'lucide-react';
import { getEventLibrary, createEvent, updateEvent, EventLibraryItem } from '../../services/cadenceService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Modal from '../common/Modal';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import WorkflowSaveBar, { SaveTarget } from '../common/WorkflowSaveBar';
import StatusSwitch from '../common/StatusSwitch';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import Pagination from '../common/Pagination';

const WF_KIND = 'sch_cadence_event';
type StatusFilter = 'active' | 'inactive' | 'all';

type FormState = { name: string; description: string; measure_periodically: boolean; is_geo_tagged: boolean };
const EMPTY: FormState = { name: '', description: '', measure_periodically: false, is_geo_tagged: false };

const CadenceEventsMaster: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission('SCH_GLOBAL_CADENCE', 'view');
    const { allowed: canModify, loading: permLoading } = usePermission('SCH_GLOBAL_CADENCE', 'view+modify');
    const { allowed: canApproveExtent } = usePermission('SCH_GLOBAL_CADENCE', 'view+modify+approve');
    const fieldVh = useFieldValidation();
    const [items, setItems] = useState<EventLibraryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [workflowRow, setWorkflowRow] = useState<EventLibraryItem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<EventLibraryItem | null>(null);
    // View-first edit: existing records open read-only; "Edit" enables the form.
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<FormState>(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Reset to page 1 whenever the result set changes shape
    useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

    const load = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const data = await getEventLibrary();
            let rows = [...data];
            if (statusFilter === 'active') rows = rows.filter(r => r.is_active === 1);
            else if (statusFilter === 'inactive') rows = rows.filter(r => r.is_active !== 1);
            if (searchTerm) {
                const q = searchTerm.toLowerCase();
                rows = rows.filter(r => r.name.toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q) || (r.seq_code || '').toLowerCase().includes(q));
            }
            rows.sort((a, b) => (b.id || 0) - (a.id || 0));
            setTotalRecords(rows.length);
            // Client-side pagination: slice the results for this page
            const paginatedRows = rows.slice((page - 1) * pageSize, page * pageSize);
            setItems(paginatedRows);
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to load events.') });
        } finally {
            setLoading(false);
        }
    }, [canView, searchTerm, statusFilter, page, pageSize]);

    useEffect(() => { load(); }, [load]);

    // Approved state — determines if form is locked
    const isApproved = editing?.workflow_state === 'approved';
    // Fields are read-only while viewing an existing record (before "Edit").
    const fieldsDisabled = !!editing && (!isEditing || !canModify || isApproved);

    const openCreate = () => { if (!canModify) return; setEditing(null); setIsEditing(true); setFormData(EMPTY); fieldVh.reset(); setIsModalOpen(true); };
    const openEdit = (ev: EventLibraryItem) => {
        setEditing(ev);
        setIsEditing(false);
        setFormData({
            name: ev.name,
            description: ev.description || '',
            measure_periodically: !!ev.measure_periodically,
            is_geo_tagged: !!ev.is_geo_tagged,
        });
        fieldVh.reset();
        setIsModalOpen(true);
    };

    const persist = async (action: SaveTarget) => {
        if (fieldsDisabled) return;
        const ok = fieldVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'description', code: 110, value: formData.description },
        ]);
        if (!ok) return;
        setSubmitting(true);
        try {
            if (editing) {
                await updateEvent(editing.id, {
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    measure_periodically: formData.measure_periodically,
                    is_geo_tagged: formData.is_geo_tagged,
                });
                setToast({ type: 'success', message: 'Event updated.' });
            } else {
                await createEvent({
                    name: formData.name.trim(),
                    description: formData.description.trim() || undefined,
                    measure_periodically: formData.measure_periodically,
                    is_geo_tagged: formData.is_geo_tagged,
                }, action);
                setToast({ type: 'success', message: ({
                    draft: 'Event saved as draft.',
                    submit: 'Event submitted for approval.',
                    approve: 'Event created & approved.',
                } as Record<SaveTarget, string>)[action] });
            }
            setIsModalOpen(false);
            await load();
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to save event.') });
        } finally {
            setSubmitting(false);
        }
    };

    // Edit form submit (Enter key in text input) → plain update.
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isEditing) return; // guard: never submit while in view mode
        persist('draft');
    };
    // Create express-save targets (Save as Draft / Submit / Approve).
    const handleCreateSave = (target: SaveTarget) => persist(target);

    const handleToggleActive = async () => {
        if (!editing || !canModify) return;
        const isActive = editing.is_active === 1;
        const next = isActive ? 0 : 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next === 1 ? 'Reactivate this event?' : 'Inactivate this event?',
            text: next === 1 ? 'It will be selectable in the cadence trigger picker again.' : 'It will be hidden from the cadence trigger picker.',
            confirmText: next === 1 ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await updateEvent(editing.id, { is_active: next });
            setToast({ type: next === 1 ? 'success' : 'warning', message: next === 1 ? 'Reactivated.' : 'Inactivated.' });
            setIsModalOpen(false);
            await load();
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have permission to view cadence events." />
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-w-0 h-full">
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
                    noun="cadence events"
                    testId="SEL_cadence_events_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search events..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_cadence_events_search"
                    />
                    <Button
                        variant="fill"
                        icon={Plus}
                        onClick={openCreate}
                        disabled={permLoading || !canModify}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        data-testid="BTN_cadence_events_create"
                    >Create</Button>
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div className="table-list-scroll" style={!loading && items.length === 0 ? { flex: '0 0 auto', display: 'none' } : undefined}>
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th" style={{ width: 90, minWidth: 90 }}>Code</th>
                                        <th className="table-list-th">Event</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && (
                                        <>
                                            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                                                <tr key={i} className="table-list-skeleton-row">
                                                    <td className="table-list-cell table-list-cell--dot">
                                                        <div className="table-list-skeleton-bar skel-dot" />
                                                    </td>
                                                    <td className="table-list-cell table-list-cell--sno">
                                                        <div className="table-list-skeleton-bar table-list-skeleton-bar--w-2xs" />
                                                    </td>
                                                    <td className="table-list-cell">
                                                        <div className="table-list-skeleton-bar table-list-skeleton-bar--w-sm" />
                                                    </td>
                                                    <td className="table-list-cell">
                                                        <div className="table-list-skeleton-bar table-list-skeleton-bar--w-md" />
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                    )}
                                    {!loading && items.map((ev, idx) => (
                                        <tr key={ev.id} className={`table-list-row${ev.is_active === 1 ? '' : ' is-inactive'}`} onClick={() => openEdit(ev)} data-testid={`BTN_cadence_events_row_${ev.id}_edit`}>
                                            <td
                                                className="table-list-cell table-list-cell--dot"
                                                onClick={(e) => { e.stopPropagation(); setWorkflowRow(ev); }}
                                                title="Workflow — view timeline & act"
                                                data-testid={`BTN_cadence_events_row_${ev.id}_workflow`}
                                            >
                                                <ApprovalDot
                                                    state={(ev.workflow_state || 'draft') as WorkflowState}
                                                    onClick={() => setWorkflowRow(ev)}
                                                />
                                            </td>
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell" style={{ width: 90, minWidth: 90 }} data-testid={`CELL_cadence_events_code_${ev.id}`}>
                                                <span className="cell-code">{ev.seq_code ?? '—'}</span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">
                                                    {ev.workflow_state === 'approved' && (
                                                        <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                    )}
                                                    {ev.name}
                                                </span>
                                                <span className="table-list-cell-sub">
                                                    {ev.measure_periodically ? 'Periodic' : 'One-time'}{ev.is_geo_tagged ? ' · Geo-tagged' : ''}
                                                    {ev.description ? ` — ${ev.description}` : ''}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {!loading && items.length === 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '300px' }}>
                                <NoDataFound title="No cadence events defined yet." message="" />
                            </div>
                        )}
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 20, 50, 100]}
                            recordLabel="cadence events"
                        />
                    </div>
                </div>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="md">
                <Modal.Header>
                    {editing
                        ? <>{isApproved ? 'Viewing (locked)' : (isEditing ? 'Editing' : 'Viewing')} "<span className="item-highlight">{editing.name}</span>"</>
                        : 'Create Cadence Event'}
                </Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <form id="cadence-event-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_cadence_events_form">
                        {/* System code — read-only, prefilled with the same code the list shows. */}
                        {editing && (
                            <FormControl.Input
                                label="Code"
                                value={(editing as any).seq_code || (editing as any).code || '—'}
                                disabled
                                data-testid="INP_cadence_events_code"
                            />
                        )}
                        <ValidatedInput
                            vh={fieldVh}
                            name="name"
                            code={109}
                            required
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g. Incident closure"
                            data-testid="INP_cadence_events_name"
                            disabled={fieldsDisabled}
                        />
                        <ValidatedTextarea
                            vh={fieldVh}
                            name="description"
                            code={110}
                            label="Description (Optional)"
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="What this event represents..."
                            data-testid="INP_cadence_events_description"
                            disabled={fieldsDisabled}
                        />
                        <label className="fg" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={formData.measure_periodically}
                                onChange={(e) => setFormData({ ...formData, measure_periodically: e.target.checked })}
                                data-testid="CHK_cadence_events_measure_periodically"
                                disabled={fieldsDisabled}
                            />
                            <span>Measure the occurrence periodically?</span>
                        </label>
                        <label className="fg" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={formData.is_geo_tagged}
                                onChange={(e) => setFormData({ ...formData, is_geo_tagged: e.target.checked })}
                                data-testid="CHK_cadence_events_is_geo_tagged"
                                disabled={fieldsDisabled}
                            />
                            <span>Geographically tagged? (collected per geography)</span>
                        </label>
                    </form>
                </Modal.Body>
                <Modal.Footer className={editing ? 'app-modal-footer--split' : ''}>
                    {editing && !isApproved && (
                        <Button
                            variant={editing.is_active === 1 ? 'inactive-outline' : 'active-outline'}
                            icon={editing.is_active === 1 ? CircleMinus : CircleCheck}
                            onClick={handleToggleActive}
                            disabled={permLoading || !canModify || toggling || submitting}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_cadence_events_toggle_active"
                        >
                            {toggling ? '...' : editing.is_active === 1 ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {/* Create → workflow save bar. */}
                        {!editing && (
                            <WorkflowSaveBar
                                canDraft={canModify}
                                canSubmit={canModify}
                                canApprove={canApproveExtent}
                                busy={submitting}
                                onSave={handleCreateSave}
                            />
                        )}
                        {/* View mode → "Edit" enables the form (but not if approved). */}
                        {editing && !isEditing && !isApproved && (
                            <Button
                                variant="fill"
                                icon={Edit2}
                                onClick={() => {
                                    if (permLoading || !canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
                                    setIsEditing(true);
                                }}
                                disabled={permLoading || !canModify}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                data-testid="BTN_cadence_events_edit"
                            >
                                Edit
                            </Button>
                        )}
                        {/* Edit mode → "Update" saves (but not if approved). */}
                        {editing && isEditing && !isApproved && (
                            <Button
                                variant="update"
                                icon={Check}
                                onClick={() => persist('draft')}
                                disabled={permLoading || !canModify || submitting || toggling}
                                title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                                data-testid="BTN_cadence_events_submit"
                            >
                                {submitting ? 'Updating...' : 'Update'}
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => { setWorkflowRow(null); load(); }}
                kind={WF_KIND}
                id={workflowRow?.id ?? null}
                onChanged={load}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default CadenceEventsMaster;
